/**
 * CountrySelect · 国家多选下拉（地区/国家二级结构）
 *
 * 完全复用 ADCountryRegion 实现：
 * - cond 数据来自 materials_config_region_countries（useCountryMap.regionOptions）
 * - UI 用 RegionSelect 组件（未连接版本，本地 state 管理）
 * - 协议与 ADCountryRegion 完全一致：
 *
 * 业务对外 API（`value`）：
 *   - `[]`              → 全选（默认）
 *   - `[UNSELECT_ALL]`  → 全不选
 *   - `[code, ...]`     → 部分选择（小写 country_code 列表）
 *
 * 内部转换：
 *   string[] ⇄ { type: 'market', regions, markets }（RegionSelect 内部格式）
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { RegionSelect } from '@opm/components/FilterCommon/widgets/RegionSelect';
import { useCountryMap, IRegionOption } from '../../hooks/useCountryMap';

/** 全不选 sentinel 值（与 ADChannel/ADCountryRegion 协议一致） */
export const UNSELECT_ALL = '-99999';

export interface ICountrySelectProps {
  /**
   * 国家值（业务对外 API）：
   * - `[]`              → 全选
   * - `[UNSELECT_ALL]`  → 全不选
   * - `[code, ...]`     → 部分选择（小写 country_code）
   */
  value: string[];
  /** 值变化回调（接收的也是协议化的 value） */
  onChange: (value: string[]) => void;
  className?: string;
}

interface IRegionMarketValue {
  type: 'market';
  regions: string[];
  markets: string[];
}

/** 计算所有 markets（扁平 country_code 列表） */
function getAllMarkets(options: IRegionOption[]): string[] {
  const all: string[] = [];
  options.forEach((r) => r.children.forEach((c) => all.push(c.value)));
  return all;
}

/** 计算所有 regions */
function getAllRegions(options: IRegionOption[]): string[] {
  return options.map((r) => r.value);
}

/** 业务 value → RegionSelect value */
function toRegionValue(
  value: string[],
  options: IRegionOption[],
): IRegionMarketValue {
  // 全不选
  if (value.length === 1 && value[0] === UNSELECT_ALL) {
    return { type: 'market', regions: [], markets: [] };
  }
  // 全选（业务空数组）
  if (value.length === 0) {
    return {
      type: 'market',
      regions: getAllRegions(options),
      markets: getAllMarkets(options),
    };
  }
  // 部分选择 → 计算 parent regions
  const regionSet = new Set<string>();
  const validMarkets: string[] = [];
  options.forEach((region) => {
    region.children.forEach((c) => {
      if (value.includes(c.value)) {
        regionSet.add(region.value);
        validMarkets.push(c.value);
      }
    });
  });
  return {
    type: 'market',
    regions: Array.from(regionSet),
    markets: validMarkets,
  };
}

/** RegionSelect value → 业务 value */
function fromRegionValue(
  next: IRegionMarketValue,
  options: IRegionOption[],
): string[] {
  const markets = next?.markets || [];
  const allMarkets = getAllMarkets(options);

  if (markets.length === 0) return [UNSELECT_ALL];
  if (allMarkets.length > 0 && markets.length === allMarkets.length) return [];
  return markets;
}

export default function CountrySelect({
  value,
  onChange,
  className,
}: ICountrySelectProps) {
  const { regionOptions } = useCountryMap();

  /** 内部暂存值：编辑中的选择（未提交给父组件） */
  const [stagingValue, setStagingValue] = useState<string[]>(value);

  /** 外部 value 变化时同步到暂存 */
  useEffect(() => {
    setStagingValue(value);
  }, [value]);

  /** 用 ref 持有最新暂存值，供 onClose 回调使用 */
  const stagingRef = useRef(stagingValue);
  stagingRef.current = stagingValue;

  /** 显示给 RegionSelect 的 value（用暂存值渲染） */
  const displayValue = useMemo(
    () => toRegionValue(stagingValue, regionOptions),
    [stagingValue, regionOptions],
  );

  /** change：仅更新暂存值 */
  const handleChange = (next: IRegionMarketValue) => {
    setStagingValue(fromRegionValue(next, regionOptions));
  };

  /** onClose：把暂存值提交给父组件（若与原值不同） */
  const handleClose = () => {
    const committed = stagingRef.current;
    if (
      committed.length !== value.length ||
      committed.some((v, i) => v !== value[i])
    ) {
      onChange(committed);
    }
  };

  return (
    <RegionSelect
      title="Country/Region"
      titleCn="国家/地区"
      valueKeys="country_codes"
      options={regionOptions}
      value={displayValue}
      change={handleChange}
      onClose={handleClose}
      // 未连接版本不依赖 redux，传空对象占位
      filterValue={{}}
      hasGlobal={false}
      maxDisplayCount={1}
      className={className}
    />
  );
}
